import React, { useState, useRef, useEffect } from "react";
import "./VoiceRecorder.css";
import VADDetector from "../api/vad";

import { voiceTranslate, OtherLanguageAsr, translateVoice, ttsBani, asrSarvam, ttsSarvam, translateSarvam, masterOrchestration } from "../api/voiceApi";

const API_BASE_URL = "https://gramasathi.niruthi.com";

const MasterOrch = ({ autoStart = false, onBack }) => {
  const [transcript, setTranscript] = useState("");
  const [language, setLanguage] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [vadStatus, setVadStatus] = useState("idle");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [model, setModel] = useState("");
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentStreamRef = useRef(null);
  const recordingProcessedRef = useRef(false);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const recognitionRef = useRef(null);
  const vadDetectorRef = useRef(null);
  const sessionIdRef = useRef(null);
  const passiveListeningRef = useRef(false);
  const inactivityTimerRef = useRef(null);
  const [isDone, setIsDone] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const chatHistoryRef = useRef([]);
  const wsRef = useRef(null);
  const wsReadyRef = useRef(false);
  const preBufferRef = useRef([]);
  const preBufferSentRef = useRef(false);
  const isSendingAudioRef = useRef(false);
  const [replayingIndex, setReplayingIndex] = useState(null);
  const chatContainerRef = useRef(null);
  const isDoneRef = useRef(false);
  const [showRegistrationToggle, setShowRegistrationToggle] = useState(false);
  const playbackPromiseRef = useRef(Promise.resolve());
  const [inputText, setInputText] = useState("");
  const [inputMode, setInputMode] = useState("voice"); // 'voice' or 'text'
  const inputModeRef = useRef("voice"); // ref mirror for use in closures
  const [isSessionStarted, setIsSessionStarted] = useState(false);
  const [selectedMobileNumber, setSelectedMobileNumber] = useState("");
  const [isSessionCompleted, setIsSessionCompleted] = useState(false);

  const generateUUID = () => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    array[6] = (array[6] & 0x0f) | 0x40;
    array[8] = (array[8] & 0x3f) | 0x80;
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('').replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5');
  };

  // Retry calling an async API function when the response is empty or missing key fields.
  // apiFn: async function that returns the response object
  // isValidResponse: function that receives the result and returns true if usable
  const callWithRetry = async (apiFn, isValidResponse, maxRetries = 3, delayMs = 1000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`callWithRetry: attempt ${attempt}/${maxRetries}`);
        const result = await apiFn();
        if (result && isValidResponse(result)) {
          console.log(`callWithRetry: valid response on attempt ${attempt}`);
          return result;
        }
        console.warn(`callWithRetry: empty/invalid response on attempt ${attempt}`, result);
      } catch (err) {
        console.warn(`callWithRetry: error on attempt ${attempt}`, err);
      }
      if (attempt < maxRetries) await new Promise(r => setTimeout(r, delayMs));
    }
    console.error(`callWithRetry: all ${maxRetries} attempts failed`);
    return null;
  };

  const isOrchestrationResponseValid = (data) => {
    if (data?.error || data?.detail) return false;
    const audio = data?.audio || {};
    return !!(
      data?.final_answer ||
      audio?.sarvam_text || audio?.baani_text ||
      audio?.sarvam_audio_base64 || audio?.banni_audio_base64
    );
  };

  const connectWebSocket = () => {
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    try {
      wsReadyRef.current = false;

      let wsUrl;
      if (API_BASE_URL && API_BASE_URL.startsWith("http")) {
        const parsed = new URL(API_BASE_URL);
        const protocol = parsed.protocol === "https:" ? "wss" : "ws";
        wsUrl = `${protocol}://${parsed.host}/ws/audio`;
      } else {
        const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
        const devHost = "localhost:8000";
        const host = window.location.hostname === "localhost" ? devHost : window.location.host;
        wsUrl = `${wsProtocol}://${host}/ws/audio`;
      }

      console.log("Connecting WS →", wsUrl);

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log("WS connected");
        wsReadyRef.current = true;
        try {
          const initMsg = JSON.stringify({ type: "init", session_id: sessionIdRef.current, lang: language || "en-IN" });
          ws.send(initMsg);
        } catch (e) { console.warn("WS init send failed", e); }
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === "text") setTranscript(data.text);
          if (data.type === "audio") playBase64Audio(data.audio);
        } catch (e) {
          console.warn("WS message parse failed", e);
        }
      };

      ws.onerror = (err) => {
        console.error("WS error", err);
      };

      ws.onclose = () => {
        console.log("WS closed");
        wsReadyRef.current = false;
        wsRef.current = null;
        if (!isDoneRef.current && inputModeRef.current === "voice") {
          console.log("WS closed unexpectedly: stopping listening processes");
          stopListeningCompletely();
        }
      };
      wsRef.current = ws;
    } catch (e) {
      console.error("connectWebSocket failed:", e);
    }
  };
  const sendAudioChunk = async (blob) => {
    if (!blob) return;
    if (!preBufferSentRef.current) {
      preBufferRef.current.push(blob);
      if (preBufferRef.current.length > 10) preBufferRef.current.shift();
    }

    try {
      if (wsRef.current && wsReadyRef.current && wsRef.current.readyState === WebSocket.OPEN && isSendingAudioRef.current) {
        const arr = await blob.arrayBuffer();
        wsRef.current.send(arr);
      }
    } catch (err) {
      console.warn("Failed to send audio chunk over WS (binary)", err);
    }
  };
  const startPassiveListening = async (existingStream = null) => {
    console.log("startPassiveListening: called");
    if (inputModeRef.current !== "voice") {
      console.log("startPassiveListening: skipped because inputMode is not voice");
      return;
    }
    passiveListeningRef.current = true;
    try {
      let stream = existingStream;

      if (!stream) {
        console.log("startPassiveListening: no existing stream, requesting new one");
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error("Microphone access is not supported in this environment. Please ensure the app is accessed over HTTPS.");
        }
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      console.log("startPassiveListening: got media stream", stream.id);
      currentStreamRef.current = stream;
      try {
        connectWebSocket();
      } catch (e) {
        console.warn("connectWebSocket failed", e);
      }

      setupMediaRecorder(stream);
      setupAudioContext(stream);

      try {
        if (vadDetectorRef.current) {
          try { vadDetectorRef.current.destroy(); } catch (e) { }
          vadDetectorRef.current = null;
        }


        const vadDetector = new VADDetector({
          threshold: 0.06,
          silenceTimeout: 500,
          smoothing: 0.70,
          useExternalAnalyser: analyserRef.current,
          onSpeechStart: () => {
            try {
              console.log("VAD: onSpeechStart — UNMUTE (enable sending)");
              if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
                inactivityTimerRef.current = null;
              }

              isSendingAudioRef.current = true;
              preBufferSentRef.current = false;

              flushPreBuffer();
              setTranscript("Recording...");
            } catch (e) { console.error(e); }
          },
          onSpeechEnd: () => {
            try {
              console.log("VAD: onSpeechEnd — scheduling MUTE and STOP in 3s");
              if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
              inactivityTimerRef.current = setTimeout(() => {
                try {
                  isSendingAudioRef.current = false;
                  console.log("mediaRecorder: MUTED (silence detected)");

                  if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
                    mediaRecorderRef.current.stop();
                    console.log("mediaRecorder: stopped by VAD (will trigger API calls)");
                  }
                } catch (e) { console.error(e); }
              }, 3000);
            } catch (e) { console.error(e); }
          }
        });

        const initialized = await vadDetector.init(stream, analyserRef.current);
        if (initialized) {
          vadDetectorRef.current = vadDetector;
          vadDetectorRef.current.start();
          console.log("startPassiveListening: VAD started");
          setVadStatus("listening");
        }
      } catch (e) {
        console.error("Passive VAD init error:", e);
      }
    } catch (err) {
      console.error("Passive listening failed to start:", {
        name: err.name,
        message: err.message,
        stack: err.stack,
        err: err
      });
      alert(err.message || "Failed to access microphone. Please check permissions and try again.");
      passiveListeningRef.current = false;
    }
  };

  const LANG_MAP = {
    "en-IN": { code: "en", backend: "en", locale: "en-IN", name: "English" },
    "te-IN": { code: "te", backend: "te", locale: "te-IN", name: "తెలుగు" },
    "hi-IN": { code: "hi", backend: "hi", locale: "hi-IN", name: "हिन्दी" },
    "or-IN": { code: "or", backend: "or", locale: "or-IN", name: "ଓଡ଼ିଆ" },
  };
  const SARVAM_LANG_MAP = {
    "en-IN": { code: "en-IN", backend: "en", locale: "en-IN", name: "English" },
    "te-IN": { code: "te-IN", backend: "te", locale: "te-IN", name: "తెలుగు" },
    "hi-IN": { code: "hi-IN", backend: "hi", locale: "hi-IN", name: "हिन्दी" },
    "od-IN": { code: "od-IN", backend: "or", locale: "od-IN", name: "ଓଡ଼ିଆ" },
  };
  const ACTIVE_LANG_MAP = model === "sarvam" ? SARVAM_LANG_MAP : LANG_MAP;
  const MODEL_MAP = {
    sarvam: { code: "sarvam", name: "Sarvam" },
    baani: { code: "baani", name: "BAANI" }
  };
  useEffect(() => {
    if (autoStart) {
      const t = setTimeout(() => {
        try {
          handleStartRecording();
        } catch (e) {
          console.warn("autoStart failed:", e);
        }
      }, 300);
      return () => clearTimeout(t);
    }
  }, [autoStart]);

  useEffect(() => {
    return () => {
      try {
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        if (vadDetectorRef.current) {
          try { vadDetectorRef.current.destroy(); } catch (e) { }
          vadDetectorRef.current = null;
        }
        if (currentStreamRef.current) {
          try { currentStreamRef.current.getTracks().forEach(t => t.stop()); } catch (e) { }
          currentStreamRef.current = null;
        }
      } catch (e) { }
    };
  }, []);
  const callTranslationWithRetry = async (formData, normalizedLang) => {
    const result = await translateVoice(formData, normalizedLang);

    console.log("callTranslationWithRetry result:", result);
    if (result?.error) {
      console.error("Translation error:", result.error);
      return result;
    }
    let transcriptText = result?.translation?.asr;

    console.log("callTranslationWithRetry transcriptText:", transcriptText, "type:", typeof transcriptText);
    return result;
  };

  const getBaaniAudio = async (text, lang, botResponseAudio = null) => {
    try {
      if (model === "sarvam") {
        console.log("Sarvam Model: using ttsSarvam");
        return await ttsSarvam(text, lang);
      }

      if (model === "baani") {
        if (lang === "or" || lang === "or-IN") {
          console.log("Baani Odia: using ttsBani");
          return await ttsBani({ text, lang });
        } else {
          console.log(`Baani ${lang}: using backend Bhashini audio`);
          return { audio_base64: botResponseAudio, isBackendAudio: true };
        }
      }

      return { audio_base64: botResponseAudio, isBackendAudio: true };
    } catch (err) {
      console.error("getBaaniAudio error:", err);
      return { error: err.message };
    }
  };

  const flushPreBuffer = async () => {
    if (!preBufferRef.current.length || preBufferSentRef.current) {
      return;
    }
    preBufferSentRef.current = true;
    try {
      if (wsRef.current && wsReadyRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log("Flushing pre-buffer with", preBufferRef.current.length, "chunks to WS (binary)");
        for (const chunk of preBufferRef.current) {
          try {
            const arr = await chunk.arrayBuffer();
            wsRef.current.send(arr);
          } catch (e) {
            console.warn("flushPreBuffer chunk send failed", e);
          }
        }
      }
    } catch (err) {
      console.warn("Failed to flush pre-buffer", err);
    }
  };
  const setupMediaRecorder = (stream) => {
    try {
      mediaRecorderRef.current = new window.MediaRecorder(stream);
      recordingProcessedRef.current = false;

      audioChunksRef.current = [];
      mediaRecorderRef.current.start(100);
      console.log("mediaRecorder: started (continuous recording)");

      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
        console.log("mediaRecorder: dataavailable, chunk size", e.data?.size);
        if (!preBufferSentRef.current) {
          preBufferRef.current.push(e.data);
          if (preBufferRef.current.length > 5) {
            preBufferRef.current.shift();
          }
        }

        try {
          sendAudioChunk(e.data);
        } catch (err) {
          console.warn("sendAudioChunk error", err);
        }

      };

      mediaRecorderRef.current.onstart = () => {
        console.log("mediaRecorder: onstart");
      };

      mediaRecorderRef.current.onstop = async () => {
        if (recordingProcessedRef.current) return;
        recordingProcessedRef.current = true;

        clearInterval(timerRef.current);

        if (!audioChunksRef.current.length) {
          setTranscript("");
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/wav" });

        if (passiveListeningRef.current) {
          try {
            console.log("Starting translation using translation API");
            setIsProcessing(true);
            setTranscript("Translating...");
            const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
            const normalizedLang = langConfig.code;

            const formData = new FormData();
            formData.append("audio", audioBlob, "recording.wav");
            formData.append("lang", normalizedLang);

            let asrText = "";
            if (model === "sarvam") {
              console.log("onstop Sarvam ASR");
              const asrRes = await asrSarvam(audioBlob, normalizedLang);
              asrText = asrRes?.transcript || asrRes?.asr || (typeof asrRes === "string" ? asrRes : "");
            } else if (model === "baani") {
              if (normalizedLang === "en" || normalizedLang === "en-IN") {
                console.log("onstop Baani: English ASR");
                const res = await voiceTranslate(formData, true);
                asrText = res?.translation?.asr || res?.asr || "";
              } else {
                console.log("onstop Baani: Regional ASR");
                const res = await OtherLanguageAsr(formData);
                asrText = res?.translation?.asr || res?.asr || "";
              }
            }

            if (!asrText || asrText.trim() === "") {
              setIsProcessing(false);
              setTranscript("Could not capture audio. Please try again.");
              audioChunksRef.current = [];
              return;
            }

            setTranscript(asrText);
            setIsProcessing(false);

            setChatHistory((prev) => [...prev, { from: "user", text: asrText, audio: audioBlob }]);
            chatHistoryRef.current = [...chatHistoryRef.current, { from: "user", text: asrText, audio: audioBlob }];

            console.log("Calling masterOrchestration with:", asrText);
            const orchestrationPayload = {
              session_id: sessionIdRef.current,
              message: asrText,
              lang: langConfig.backend,
              model: model,
              mobile_number: selectedMobileNumber,
            };
            const data = await callWithRetry(
              () => masterOrchestration(orchestrationPayload),
              isOrchestrationResponseValid
            );
            console.log("Orchestration response:", data);

            if (!data) {
              console.error("Orchestration failed after all retries (possibly timeout)");
              setIsProcessing(false);
              setTranscript("Service is taking too long. Please try again.");
              audioChunksRef.current = [];
              if (!isDone) await startPassiveListening();
              return;
            }

            if (data?.error) {
              console.error("Backend error:", data.error);
              setIsProcessing(false);
              setTranscript("An error occurred. Please try again.");
              audioChunksRef.current = [];
              return;
            }
            const botResponse = data;
            const audioData = botResponse.audio || {};
            let botText = botResponse.final_answer || "";
            let botAudio = "";

            if (model === "baani") {
              botText = audioData.baani_text || botText;
              botAudio = audioData.banni_audio_base64 || "";
            } else if (model === "sarvam") {
              botText = audioData.sarvam_text || botText;
              botAudio = audioData.sarvam_audio_base64 || "";
            }

            const entry = {
              from: "bot",
              text: botText,
              audio: botAudio,
              originalAudio: botAudio,
              model: model
            };
            setChatHistory((prev) => [...prev, entry]);
            chatHistoryRef.current = [...chatHistoryRef.current, entry];

            const originalText = (botResponse.final_answer || "").toLowerCase();

            const isRegistrationQuestion =
              originalText.includes("registred ot not") ||
              originalText.includes("registered or not registered");

            if (isRegistrationQuestion) {
              setShowRegistrationToggle(true);
            }

            try {
              setIsProcessing(false);
              setVadStatus("processing");

              try {
                vadDetectorRef.current?.stop();
              } catch (e) { }

              let audioBase64 = botAudio;
              let usedModel = model;

              if ((model === "baani" && (normalizedLang === "en" || normalizedLang === "en-IN")) || !audioBase64) {
                if (botText) {
                  console.log(`Generating TTS for ${model}...`);
                  const ttsRes = await getBaaniAudio(botText, normalizedLang);
                  if (ttsRes && ttsRes.audio_base64) {
                    audioBase64 = ttsRes.audio_base64;
                    usedModel = ttsRes.isBackendAudio ? 'bhashini' : model;
                  }
                }
              }

              if (audioBase64) {
                await playBase64Audio(audioBase64);
                // Update cache with the played audio
                setChatHistory(prev => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (updated[lastIdx]?.from === "bot") {
                    updated[lastIdx] = {
                      ...updated[lastIdx],
                      audio: audioBase64,
                      model: usedModel
                    };
                  }
                  chatHistoryRef.current = updated;
                  return updated;
                });
              } else {
                console.warn("No audio available to play");
              }

            } catch (err) {
              console.error("Audio flow error:", err);
              if (botAudio) await playBase64Audio(botAudio);
            } finally {
              const isGoodbye = typeof botText === "string" && botText.includes("Goodbye!");
              const isBookingSuccess = originalText.includes("your ai booking was successful");
              const isWaitingForBooking = originalText.includes("please wait for a moment. your ai booking is being processed.");
              const isCallThankYou = originalText.includes("thank you for calling krushi samrudhhi helpline, feel free to call again anytime!");

              if (isGoodbye || isBookingSuccess || isCallThankYou) {
                console.log("onstop bot finished speaking: End message received, stopping audio recording.");
                setIsRecording(false);
                isDoneRef.current = true;
                setIsDone(true);
                setIsSessionStarted(false);
                setIsSessionCompleted(true);
                sessionIdRef.current = null;
                try {
                  stopListeningCompletely();
                } catch (e) { }
              } else if (isWaitingForBooking) {
                console.log("onstop: detected 'please wait' processing message, triggering auto-followup");
                await autoFollowupOrchestration(langConfig);
                if (!isDoneRef.current && inputModeRef.current === "voice") await startPassiveListening();
              } else if (!isDoneRef.current && inputModeRef.current === "voice") {
                console.log("onstop bot finished speaking: starting passive listening");
                await startPassiveListening();
              }
            }
          } catch (err) {
            console.error("Transcription or orchestration call failed:", err);
            setIsProcessing(false);
            setTranscript("Unable to process. Please try again.");
          } finally {
            audioChunksRef.current = [];
          }
          return;
        }

        try {
          if (recognitionRef.current) {
            try {
              recognitionRef.current.stop();
            } catch (e) { }
            return;
          }

          setTranscript("Transcription unavailable in this browser. Please type your query.");
        } catch (err) {
          console.error(err);
        }
      };
    } catch (e) {
      console.error("setupMediaRecorder error", e);
      mediaRecorderRef.current = null;
    }
  };

  const playBase64Audio = async (base64ArrayOrSingle) => {
    playbackPromiseRef.current = playbackPromiseRef.current.then(async () => {
      const dataList = Array.isArray(base64ArrayOrSingle) ? base64ArrayOrSingle : [base64ArrayOrSingle];
      if (!dataList.length || !dataList.some(d => d)) return;

      setIsAudioPlaying(true);
      try {
        for (const base64Data of dataList) {
          if (!base64Data) continue;
          await new Promise((resolve) => {
            try {
              const byteCharacters = atob(base64Data);
              const byteNumbers = new Uint8Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }

              let mimeType = "audio/wav";
              const headerStr = base64Data.substring(0, 12);
              if (headerStr.startsWith("ID3") || base64Data.substring(0, 2) === "//") {
                mimeType = "audio/mp3";
              } else if (headerStr.startsWith("ftyp")) {
                mimeType = "audio/mp4";
              }

              const audioBlob = new Blob([byteNumbers], { type: mimeType });
              const audioUrl = URL.createObjectURL(audioBlob);
              const audio = new Audio(audioUrl);
              audio.preload = "auto";
              audio.volume = 1.0;
              audio.crossOrigin = "anonymous";

              let audioEnded = false;
              const cleanup = () => {
                try { URL.revokeObjectURL(audioUrl); } catch (e) { }
              };

              audio.addEventListener("ended", () => {
                audioEnded = true;
                cleanup();
                resolve();
              });

              audio.addEventListener("error", (e) => {
                console.error("playBase64Audio track error:", e);
                cleanup();
                resolve();
              });

              const timeoutId = setTimeout(() => {
                if (!audioEnded) {
                  audioEnded = true;
                  cleanup();
                  resolve();
                }
              }, 45000);

              audio.play().catch((e) => {
                console.error("playBase64Audio play() error:", e);
                clearTimeout(timeoutId);
                cleanup();
                resolve();
              });
            } catch (err) {
              console.error("playBase64Audio inner error:", err);
              resolve();
            }
          });
        }
      } finally {
        setIsAudioPlaying(false);
      }
    }).catch(err => {
      console.error("Global playback chain error:", err);
      return Promise.resolve();
    });
    return playbackPromiseRef.current;
  };
  // const playBase64Audio = (text) => {
  //   return new Promise(async (resolve, reject) => {
  //     try {
  //       // Call the TTS API with the text
  //       const response = await fetch('http://183.82.109.218:8092/', {
  //         method: 'POST',
  //         headers: {
  //           'Content-Type': 'application/json',
  //         },
  //         body: JSON.stringify({ text: text }),
  //       });

  //       if (!response.ok) {
  //         throw new Error(`API request failed with status ${response.status}`);
  //       }

  //       // Get the audio as a blob directly
  //       const audioBlob = await response.blob();
  //       const audioUrl = URL.createObjectURL(audioBlob);

  //       const audio = new Audio(audioUrl);
  //       audio.preload = "auto";

  //       const cleanup = () => {
  //         try {
  //           URL.revokeObjectURL(audioUrl);
  //         } catch (e) { }
  //         setIsAudioPlaying(false);
  //       };

  //       audio.addEventListener("ended", () => {
  //         cleanup();
  //         resolve();
  //       });

  //       audio.addEventListener("error", (e) => {
  //         cleanup();
  //         reject(e);
  //       });

  //       setIsAudioPlaying(true);
  //       audio.play().catch((e) => {
  //         cleanup();
  //         reject(e);
  //       });
  //     } catch (error) {
  //       reject(error);
  //     }
  //   });
  // };

  const setupAudioContext = (stream) => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
    }
    if (!analyserRef.current) {
      analyserRef.current = audioCtxRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
    }
    if (!sourceRef.current) {
      sourceRef.current = audioCtxRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);
    }
  };

  const stopListeningCompletely = () => {
    console.log(" stopListeningCompletely: stopping VAD, recorder, stream and WS");

    passiveListeningRef.current = false;

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }

    if (vadDetectorRef.current) {
      try { vadDetectorRef.current.destroy(); } catch (e) { }
      vadDetectorRef.current = null;
    }

    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    } catch (e) { console.warn("stopListeningCompletely: mediaRecorder stop failed", e); }

    if (currentStreamRef.current) {
      try { currentStreamRef.current.getTracks().forEach(t => t.stop()); } catch (e) { }
      currentStreamRef.current = null;
    }

    if (wsRef.current) {
      try { wsRef.current.close(); } catch (e) { }
      wsRef.current = null;
    }

    try {
      audioCtxRef.current?.suspend && audioCtxRef.current.suspend();
      audioCtxRef.current = null;
    } catch (e) { }

    if (sourceRef.current) {
      try { sourceRef.current.disconnect(); } catch (e) { }
      sourceRef.current = null;
    }

    preBufferRef.current = [];
    preBufferSentRef.current = false;
    inputModeRef.current = "voice";
    setShowRegistrationToggle(false);
    audioChunksRef.current = [];

    setIsRecording(false);
    setVadStatus("done");
  };

  const autoFollowupOrchestration = async (langConfig) => {
    console.log("autoFollowupOrchestration: triggering follow-up with 'Ai-booking ticket'");
    setIsProcessing(true);
    const followupData = await callWithRetry(
      () => masterOrchestration({ session_id: sessionIdRef.current, message: "Ai-booking ticket", lang: langConfig.backend, model: model, mobile_number: selectedMobileNumber }),
      isOrchestrationResponseValid
    );
    if (!followupData) { setIsProcessing(false); return null; }
    const fAudio = followupData.audio || {};
    let fText = followupData.final_answer || "";
    let fBotAudio = "";
    if (model === "baani") { fText = fAudio.baani_text || fText; fBotAudio = fAudio.banni_audio_base64 || ""; }
    else if (model === "sarvam") { fText = fAudio.sarvam_text || fText; fBotAudio = fAudio.sarvam_audio_base64 || ""; }
    const fEntry = { from: "bot", text: fText, audio: fBotAudio, originalAudio: fBotAudio, model };
    setChatHistory(prev => [...prev, fEntry]);
    chatHistoryRef.current = [...chatHistoryRef.current, fEntry];
    setTranscript(fText);
    if (fBotAudio) await playBase64Audio(fBotAudio);
    setIsProcessing(false);
    return fText;
  };

  const handleStartRecording = async () => {
    if (!model || !language) {
      alert("Please select both Model and Language first.");
      return;
    }
    setInputMode("voice");
    inputModeRef.current = "voice";
    setIsDone(false);
    isDoneRef.current = false;
    setIsProcessing(false);
    setIsSessionCompleted(false);

    const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
    const normalizedLang = langConfig.code;
    if (!isRecording) {
      if (!sessionIdRef.current) {
        sessionIdRef.current = generateUUID();
      }
      setTranscript("");
      setIsRecording(true);
      // setChatHistory((prev) => [
      //   ...prev,
      //   { from: "system", text: "=========== New Session ===========" }
      // ]);
      audioChunksRef.current = [];
      try {
        console.log("handleStartRecording: requesting microphone access immediately");
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error("Microphone access is not supported in this environment.");
        }
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log("handleStartRecording: got media stream", stream.id);
        currentStreamRef.current = stream;

        console.log("handleStartRecording: calling welcome API");
        const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];

        let greeting = "hai";
        if (langConfig.backend === "te") greeting = "హాయ్";
        else if (langConfig.backend === "hi") greeting = "हाय";
        else if (langConfig.backend === "or") greeting = "ହାଇ";

        const welcomePayload = {
          session_id: sessionIdRef.current,
          message: greeting,
          lang: langConfig.backend,
          model: model,
          mobile_number: selectedMobileNumber,
        };
        const welcomeData = await callWithRetry(
          () => masterOrchestration(welcomePayload),
          isOrchestrationResponseValid
        );
        console.log("Welcome API response:", welcomeData);

        const audioData = welcomeData.audio || {};
        let welcomeText = welcomeData.final_answer || "";
        let originalWelcomeAudio = "";

        if (model === "baani") {
          welcomeText = audioData.baani_text || welcomeText;
          originalWelcomeAudio = audioData.banni_audio_base64 || "";
        } else if (model === "sarvam") {
          welcomeText = audioData.sarvam_text || welcomeText;
          originalWelcomeAudio = audioData.sarvam_audio_base64 || "";
        }

        setTranscript(welcomeText);

        let welcomeAudioToPlay = originalWelcomeAudio;
        let usedWelcomeModel = model;

        if (!welcomeAudioToPlay && welcomeText) {
          try {
            console.log(`${model} Welcome: Generating audio...`);
            const ttsRes = await getBaaniAudio(welcomeText, normalizedLang);
            if (ttsRes && ttsRes.audio_base64) {
              welcomeAudioToPlay = ttsRes.audio_base64;
              usedWelcomeModel = ttsRes.isBackendAudio ? 'bhashini' : model;
            }
          } catch (e) {
            console.error("Welcome TTS error:", e);
          }
        }

        const welcomeEntry = {
          from: "bot",
          text: welcomeText,
          audio: welcomeAudioToPlay,
          originalAudio: originalWelcomeAudio,
          model: usedWelcomeModel
        };
        setChatHistory((prev) => [...prev, welcomeEntry]);
        chatHistoryRef.current = [...chatHistoryRef.current, welcomeEntry];

        if (welcomeAudioToPlay) {
          try {
            console.log("Playing welcome audio...");
            await playBase64Audio(welcomeAudioToPlay);
          } catch (err) {
            console.error("Welcome audio playback failed:", err);
          }
        }

        // Start passive listening for voice interaction, passing the stream we already got
        console.log("handleStartRecording: starting passive listening with existing stream");
        await startPassiveListening(stream);
      } catch (err) {
        console.error("handleStartRecording: error:", {
          name: err.name,
          message: err.message,
          stack: err.stack,
          err: err
        });
        alert(err.message || "Please enable microphone permission and try again.");
        setIsRecording(false);
      }
    } else {
      console.log("handleStartRecording: stopping listening (user click)");
      setIsRecording(false);
      clearInterval(timerRef.current);
      try {
        stopListeningCompletely();
      } catch (e) {
        console.error("Stop error", e);
      }

      console.log("handleStartRecording: stopped and cleaned up");
    }
  };


  const handleStartSession = async () => {
    if (!model || !language) {
      alert("Please select both Model and Language first.");
      return;
    }

    try {
      setIsProcessing(true);

      // Start a session if not already started
      if (!sessionIdRef.current) {
        sessionIdRef.current = generateUUID();
      }

      console.log("Starting session with masterOrchestration: 'hai'");
      const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
      const orchestrationPayload = {
        session_id: sessionIdRef.current,
        message: "hai",
        lang: langConfig.backend,
        model: model,
        mobile_number: selectedMobileNumber,
      };

      const data = await callWithRetry(
        () => masterOrchestration(orchestrationPayload),
        isOrchestrationResponseValid
      );

      if (!data) {
        console.error("Session start failed after all retries");
        setIsProcessing(false);
        alert("Failed to start session. Please try again.");
        return;
      }

      // Handle Bot Response
      const audioData = data.audio || {};
      let botText = data.final_answer || "";
      let botAudio = "";

      if (model === "baani") {
        botText = audioData.baani_text || botText;
        botAudio = audioData.banni_audio_base64 || "";
      } else if (model === "sarvam") {
        botText = audioData.sarvam_text || botText;
        botAudio = audioData.sarvam_audio_base64 || "";
      }

      // Add to chat history
      if (botText) {
        setChatHistory((prev) => [...prev, { from: "bot", text: botText }]);
        chatHistoryRef.current = [...chatHistoryRef.current, { from: "bot", text: botText }];
      }

      // Play audio if available
      if (botAudio) {
        await playBase64Audio(botAudio);
      }

      setIsProcessing(false);
      setIsSessionStarted(true);

    } catch (err) {
      console.error("Failed to start session:", err);
      setIsProcessing(false);
      alert("Failed to start session. Please try again.");
    }
  };

  const handleTextSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!model || !language) {
      alert("Please select both Model and Language first.");
      return;
    }
    if (!inputText.trim() || isProcessing) return;

    const textToSubmit = inputText.trim();
    setInputText("");
    setInputMode("text");
    inputModeRef.current = "text";

    // Immediately stop voice recording if active
    stopListeningCompletely();

    // Start a session if not already started
    if (!sessionIdRef.current) {
      sessionIdRef.current = generateUUID();
    }

    try {
      setIsProcessing(true);
      setTranscript(textToSubmit);

      // Add user message to UI
      const userEntry = { from: "user", text: textToSubmit };
      setChatHistory((prev) => [...prev, userEntry]);
      chatHistoryRef.current = [...chatHistoryRef.current, userEntry];

      const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];
      const orchestrationPayload = {
        session_id: sessionIdRef.current,
        message: textToSubmit,
        lang: langConfig.backend,
        model: model,
        mobile_number: selectedMobileNumber,
      };

      console.log("handleTextSubmit: calling masterOrchestration with", textToSubmit);
      const data = await callWithRetry(
        () => masterOrchestration(orchestrationPayload),
        isOrchestrationResponseValid
      );

      if (!data) {
        setTranscript("Service error. Please try again.");
        setIsProcessing(false);
        return;
      }

      // Handle Bot Response
      const audioData = data.audio || {};
      let botText = data.final_answer || "";
      let botAudio = "";

      if (model === "baani") {
        botText = audioData.baani_text || botText;
        botAudio = audioData.banni_audio_base64 || "";
      } else if (model === "sarvam") {
        botText = audioData.sarvam_text || botText;
        botAudio = audioData.sarvam_audio_base64 || "";
      }

      const botEntry = {
        from: "bot",
        text: botText,
        audio: botAudio,
        originalAudio: botAudio,
        model: model
      };
      setChatHistory((prev) => [...prev, botEntry]);
      chatHistoryRef.current = [...chatHistoryRef.current, botEntry];

      const originalText = (data.final_answer || "").toLowerCase();

      // Check for registration question
      if (originalText.includes("please select the option your are registred ot not")) {
        setShowRegistrationToggle(true);
      }

      setIsProcessing(false);

      if (botAudio) {
        await playBase64Audio(botAudio);
      }

      // Auto-followup: when the API returns a "processing" message, re-call with "Ai-booking ticket"
      if (originalText.includes("please wait for a moment. your ai booking is being processed.")) {
        console.log("handleTextSubmit: detected processing message, triggering auto-followup");
        await autoFollowupOrchestration(langConfig);
        return;
      }

      const isGoodbye = typeof botText === "string" && botText.includes("Goodbye!");
      const isBookingSuccess = originalText.includes("your ai booking was successful");
      const isCallThankYou = originalText.includes("thank you for calling krushi samrudhhi helpline, feel free to call again anytime!");

      if (isGoodbye || isBookingSuccess || isCallThankYou) {
        setIsDone(true);
        isDoneRef.current = true;
        setIsSessionStarted(false);
        setIsSessionCompleted(true);
        sessionIdRef.current = null;
        try { stopListeningCompletely(); } catch (e) { }
        return;
      }
    } catch (err) {
      console.error("handleTextSubmit error:", err);
      setIsProcessing(false);
    }
  };
  const handleRegistrationSelection = async (value, label) => {
    setShowRegistrationToggle(false);
    console.log("Registration selection:", value);

    // Add user's selection to chat
    setChatHistory((prev) => [...prev, { from: "user", text: label }]);
    chatHistoryRef.current = [...chatHistoryRef.current, { from: "user", text: label }];

    try {
      setIsProcessing(true);
      setTranscript(`Sending ${label}...`);

      const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];

      const orchestrationPayload = {
        session_id: sessionIdRef.current,
        message: value,
        lang: langConfig.backend,
        model: model,
        mobile_number: selectedMobileNumber,
      };

      const data = await callWithRetry(
        () => masterOrchestration(orchestrationPayload),
        isOrchestrationResponseValid
      );
      console.log("Registration API response:", data);

      if (!data) {
        setIsProcessing(false);
        setTranscript("Service is taking too long. Please try again.");
        if (!isDone) await startPassiveListening(currentStreamRef.current);
        return;
      }

      const botResponse = data;
      const audioData = botResponse.audio || {};
      let botText = botResponse.final_answer || "";
      let botAudio = "";

      if (model === "baani") {
        botText = audioData.baani_text || botText;
        botAudio = audioData.banni_audio_base64 || "";
      } else if (model === "sarvam") {
        botText = audioData.sarvam_text || botText;
        botAudio = audioData.sarvam_audio_base64 || "";
      }

      setTranscript(botText);

      const respEntry = {
        from: "bot",
        text: botText,
        audio: botAudio,
        originalAudio: botAudio,
        model: model
      };
      setChatHistory((prev) => [...prev, respEntry]);
      chatHistoryRef.current = [...chatHistoryRef.current, respEntry];

      if (botAudio) {
        try {
          await playBase64Audio(botAudio);
        } catch (err) {
          console.error("Response audio playback failed:", err);
        }
      }

      // Auto-followup: when the API returns a "processing" message, re-call with "Ai-booking ticket"
      const originalText = (botResponse.final_answer || "").toLowerCase();
      if (originalText.includes("please wait for a moment. your ai booking is being processed.")) {
        console.log("handleRegistrationSelection: detected processing message, triggering auto-followup");
        await autoFollowupOrchestration(langConfig);
        if (!isDone) await startPassiveListening(currentStreamRef.current);
        return;
      }

      const isGoodbye = typeof botText === "string" && botText.includes("Goodbye!");
      const isBookingSuccess = originalText.includes("your ai booking was successful");
      const isCallThankYou = originalText.includes("thank you for calling krushi samrudhhi helpline, feel free to call again anytime!");

      if (isGoodbye || isBookingSuccess || isCallThankYou) {
        setIsDone(true);
        isDoneRef.current = true;
        setIsSessionStarted(false);
        sessionIdRef.current = null;
        try { stopListeningCompletely(); } catch (e) { }
        return;
      }

      setIsProcessing(false);

      // Resume passive listening after bot speaks
      if (!isDone && !isDoneRef.current) {
        await startPassiveListening(currentStreamRef.current);
      }

    } catch (err) {
      console.error("Registration selection API call failed:", err);
      setIsProcessing(false);
      setTranscript("Error processing selection. Please try again.");
    }
  };

  const handleBackToHome = () => {
    if (typeof onBack === 'function') {
      onBack();
    } else {
      window.location.href = "/";
    }
  };
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        !e.target.closest(".lang-dropdown") &&
        !e.target.closest(".chatgpt-bar")
      ) {
        setDropdownOpen(false);
        setModelDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);
  const handleReplayMessage = async (index) => {
    const message = chatHistory[index];
    if (!message) return;

    try {
      setReplayingIndex(index);

      if (message.from === "user" && message.audio instanceof Blob) {
        // Replay user audio (blob)
        const audioUrl = URL.createObjectURL(message.audio);
        const audio = new Audio(audioUrl);

        audio.addEventListener("ended", () => {
          URL.revokeObjectURL(audioUrl);
          setReplayingIndex(null);
        });

        audio.addEventListener("error", () => {
          URL.revokeObjectURL(audioUrl);
          setReplayingIndex(null);
        });

        await audio.play().catch((e) => {
          console.error("User audio replay failed:", e);
          setReplayingIndex(null);
        });
      } else if (message.from === "bot") {
        try {
          console.log(`Replay: Using ${model} routing logic...`);
          const langConfig = ACTIVE_LANG_MAP[language] || ACTIVE_LANG_MAP["en-IN"];

          // Use the unified helper to decide which audio to play/generate
          const ttsRes = await getBaaniAudio(
            message.text || "",
            langConfig.code,
            message.originalAudio || message.audio
          );

          let audioToPlay = ttsRes?.audio_base64 || ttsRes?.ask?.audio_base64 || message.audio;

          if (audioToPlay) {
            await playBase64Audio(audioToPlay);

            // Optionally update cache if we generated new audio
            if (audioToPlay !== message.audio) {
              setChatHistory(prev => {
                const updated = [...prev];
                updated[index] = {
                  ...updated[index],
                  audio: audioToPlay,
                  model: ttsRes?.isBackendAudio ? 'bhashini' : model
                };
                chatHistoryRef.current = updated;
                return updated;
              });
            }
          } else {
            console.warn("Replay: No audio available for the current model/language combination");
          }
        } catch (err) {
          console.error("Replay failed:", err);
        } finally {
          setReplayingIndex(null);
        }
      }
    } catch (err) {
      console.error("Replay failed:", err);
      setReplayingIndex(null);
    }
  };
  const REGISTRATION_LABELS = {
    "en-IN": {
      registered: { label: "Registered", value: "yes" },
      notRegistered: { label: "Not Registered", value: "no" }
    },
    "te-IN": {
      registered: { label: "నమోదు చేసుకున్నాను", value: "అవును" },
      notRegistered: { label: "నమోదు కాలేదు", value: "కాదు" }
    },
    "hi-IN": {
      registered: { label: "पंजीकृत", value: "हाँ।" },
      notRegistered: { label: "अपंजीकृत", value: "नहीं" }
    },
    "or-IN": {
      registered: { label: "ନିବନ୍ଧିତ", value: "ହଁ" },
      notRegistered: { label: "ଅନିବନ୍ଧିତ", value: "ନା" }
    },
    "od-IN": {
      registered: { label: "ନିବନ୍ଧିତ", value: "ହଁ" },
      notRegistered: { label: "ଅନିବନ୍ଧିତ", value: "ନା" }
    }
  };
  const currentLabels = REGISTRATION_LABELS[language] || REGISTRATION_LABELS["en-IN"];
  const MOBILE_NUMBER = {
    "8179445643": "",
    "6305251679": "",
    "9676650473": "",
    "8270838686": "Nuagaon(v),Khordha(d)",
    "8908014814": "Banamalipur(v),Cuttack(d)",
    "9040008685": "Delang(v),Puri(d)",
  };
  return (
    <div className="voice-root">
      {/* Decorative Background Glows */}
      <div className="bg-decoration">
        <div className="bg-glow-1"></div>
        <div className="bg-glow-2"></div>
      </div>

      <main className="voice-main">
        <div className="studio-container">

          {/* Interaction Column */}
          <div className="interaction-studio">
            <div className="studio-header">
              <div className="studio-title-row">
                <button type="button" className="voice-back-btn" onClick={handleBackToHome} aria-label="Back to home">
                  <span className="material-symbols-outlined">arrow_back</span>
                  <span>Back</span>
                </button>
                <h1 className="studio-title">
                </h1>
                <h3 className="studio-sub-title">
                  Krushi Samrudhi Helpline
                </h3>
              </div>
              <p className="studio-subtitle">
                Your direct connection to agricultural expertise. Select your mode and start your query.
              </p>
            </div>

            {/* Input Mode Toggle */}
            <div className="mode-toggle-wrapper">
              <div className="mode-toggle-pill">
                <button
                  className={`toggle-btn ${inputMode === 'voice' ? 'active' : ''}`}
                  onClick={() => {
                    setInputMode('voice');
                    inputModeRef.current = 'voice';
                    stopListeningCompletely();
                    // Reset session when switching to voice mode
                    setIsSessionStarted(false);
                    setIsSessionCompleted(false);
                    setChatHistory([]);
                    chatHistoryRef.current = [];
                    sessionIdRef.current = null;
                  }}
                >
                  <span className="material-symbols-outlined">mic</span>
                  Voice Mode
                </button>
                <button
                  className={`toggle-btn ${inputMode === 'text' ? 'active' : ''}`}
                  onClick={() => {
                    setInputMode('text');
                    inputModeRef.current = 'text';
                    stopListeningCompletely();
                    // Reset session when switching to text mode
                    setIsSessionStarted(false);
                    setIsSessionCompleted(false);
                    setChatHistory([]);
                    chatHistoryRef.current = [];
                    sessionIdRef.current = null;
                  }}
                >
                  <span className="material-symbols-outlined">keyboard</span>
                  Text Mode
                </button>
              </div>
            </div>

            {/* Main Interaction Card */}
            <div className="studio-card">

              {inputMode === 'voice' ? (
                /* Voice Interaction View */
                <div className="voice-unit" style={{ animation: 'fadeIn 0.4s ease-out' }}>
                  <div className="mic-orb-container">
                    <div className={`orb-pulse ${isRecording ? 'active' : ''}`}></div>
                    <button
                      className="mic-orb"
                      onClick={handleStartRecording}
                      disabled={!model || !language || isAudioPlaying || isProcessing || isDone}
                      aria-label="Start voice interaction"
                    >
                      <span className="material-symbols-outlined">
                        {isRecording ? 'graphic_eq' : 'mic'}
                      </span>
                      <div className="tap-label">Tap to Speak</div>
                    </button>
                  </div>

                  <div className="status-indicator">
                    <div className="status-text">
                      {isSessionCompleted ? "Process completed for this session" :
                        isAudioPlaying ? "Assistant is Speaking..." :
                          isProcessing ? "Processing your query..." :
                            isRecording ? "Listening..." : "Waiting for voice input"}
                    </div>
                    {isRecording && (
                      <div className="audio-waves">
                        {[...Array(6)].map((_, i) => (
                          <div key={i} className="wave-bar active" style={{ animationDelay: `${i * 0.1}s` }}></div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Text Interaction View */
                <div className="text-unit" style={{ animation: 'fadeIn 0.4s ease-out' }}>
                  {isSessionStarted ? (
                    <div className="setting-field">
                      <label className="studio-label">Describe your query</label>
                      <div className="textarea-wrapper">
                        <textarea
                          className="studio-textarea"
                          placeholder="Ex: I need help with soil testing for my paddy crop..."
                          value={inputText}
                          onChange={(e) => setInputText(e.target.value)}
                          disabled={isProcessing}
                        ></textarea>
                      </div>
                    </div>
                  ) : (
                    <div className="start-session-prompt">
                      <div className="start-icon">
                        <span className="material-symbols-outlined">chat</span>
                      </div>
                      <h3>Ready to start your conversation?</h3>
                      <p>Select your AI Model and Language, then click Start to begin.</p>
                    </div>
                  )}
                </div>
              )}


              {/* Shared Settings Grid */}
              <div className="input-settings-grid">
                <div className="setting-field">
                  <label className="studio-label">Mobile Number</label>
                  <div className="premium-select-wrapper">
                    <select
                      className="premium-select"
                      value={selectedMobileNumber}
                      onChange={(e) => setSelectedMobileNumber(e.target.value)}
                      disabled={!!sessionIdRef.current}
                    >
                      <option value="">Select Your Mobile Number</option>
                      {Object.entries(MOBILE_NUMBER).map(([key]) => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined select-arrow">expand_more</span>
                  </div>
                </div>

                <div className="setting-field">
                  <label className="studio-label">Village</label>
                  <div className="premium-select-wrapper">
                    <input
                      type="text"
                      className="premium-select"
                      style={{ background: "#f5f5f4", cursor: "default" }}
                      value={MOBILE_NUMBER[selectedMobileNumber] || "Not Registered Number"}
                      readOnly
                    />
                  </div>
                </div>
                <div className="setting-field">
                  <label className="studio-label">AI Model</label>
                  <div className="premium-select-wrapper">
                    <select
                      className="premium-select"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      disabled={!!sessionIdRef.current}
                    >
                      <option value="">Select AI Model</option>
                      {Object.entries(MODEL_MAP).map(([key, val]) => (
                        <option key={key} value={key}>{val.name}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined select-arrow">expand_more</span>
                  </div>
                </div>

                <div className="setting-field">
                  <label className="studio-label">Your Language</label>
                  <div className="premium-select-wrapper">
                    <select
                      className="premium-select"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      disabled={!!sessionIdRef.current}
                    >
                      <option value="">Select Language</option>
                      {Object.entries(ACTIVE_LANG_MAP).map(([key, val]) => (
                        <option key={key} value={key}>{val.name}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined select-arrow">expand_more</span>
                  </div>
                </div>
              </div>

              {/* Action Footer for Text Mode */}
              {inputMode === 'text' && (
                <div className="studio-footer">
                  <div className="status-badges">
                    <div className="badge badge-emerald">
                      <div className="badge-dot"></div>
                      {isSessionStarted ? "Session Active" : "AI Ready"}
                    </div>
                  </div>
                  {!isSessionStarted ? (
                    <button
                      className="process-button"
                      onClick={handleStartSession}
                      disabled={isProcessing || !model || !language}
                    >
                      {isProcessing ? "Starting..." : "Start"}
                      <span className="material-symbols-outlined">play_arrow</span>
                    </button>
                  ) : (
                    <button
                      className="process-button"
                      onClick={handleTextSubmit}
                      disabled={isProcessing || !inputText.trim() || !model || !language}
                    >
                      {isProcessing ? "Processing..." : "Process Query"}
                      <span className="material-symbols-outlined">send</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* History Column */}
          <aside className="chat-panel-container">
            <div className="chat-panel">
              <div className="chat-header">
                <span>CONVERSATION STREAM</span>
                {/* <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>history</span> */}
              </div>
              <div className="chat-scroll-area" ref={chatContainerRef}>
                {chatHistory.length === 0 ? (
                  <div className="empty-chat">
                    <span className="material-symbols-outlined" style={{ fontSize: '48px', opacity: 0.2 }}>
                      forum
                    </span>
                    <p>No messages yet</p>
                  </div>
                ) : (
                  chatHistory.map((msg, idx) => (
                    <div key={idx} className={`chat-bubble-wrapper ${msg.from}`}>
                      <div className="chat-bubble" style={msg.from === 'bot' || msg.from === 'system' ? { textAlign: 'justify' } : {}}>
                        {msg.text}
                        {msg.from !== "system" && (
                          <button
                            className={`replay-btn ${replayingIndex === idx ? "playing" : ""}`}
                            onClick={() => handleReplayMessage(idx)}
                            disabled={replayingIndex !== null && replayingIndex !== idx}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>
                              {replayingIndex === idx ? 'volume_up' : 'play_circle'}
                            </span>
                            {replayingIndex === idx ? "Playing..." : "Replay"}
                          </button>
                        )}
                      </div>

                      {/* Registration UI if needed */}
                      {idx === chatHistory.length - 1 && showRegistrationToggle && (
                        <div className="registration-toggle-box">
                          <button
                            className="reg-btn"
                            onClick={() => handleRegistrationSelection(currentLabels.registered.value, currentLabels.registered.label)}
                          >
                            {currentLabels.registered.label}
                          </button>

                          <button
                            className="reg-btn"
                            onClick={() => handleRegistrationSelection(currentLabels.notRegistered.value, currentLabels.notRegistered.label)}
                          >
                            {currentLabels.notRegistered.label}
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default MasterOrch;
